import type { CompletedHandRecord, PlayerStatsSummary, StatsRepository } from '@texas42/app-model';
import { derivePlayerStatsSummary } from '@texas42/app-model';
import type { Texas42Database } from '../db.js';
import { STORE_COMPLETED_HANDS } from '../constants.js';
import { listEnvelopes, readEnvelopeOrNull, writeEnvelope } from '../envelope-io.js';
import type { WriteQueue } from '../write-queue.js';
import { StorageUnavailableError } from '../storage-errors.js';

export function createIndexedDbStatsRepository(
  db: Texas42Database,
  queue: WriteQueue,
): StatsRepository {
  async function loadHands(): Promise<CompletedHandRecord[]> {
    return listEnvelopes<CompletedHandRecord>(
      db,
      STORE_COMPLETED_HANDS,
      'completedHand',
      (hand) => hand.id,
    );
  }

  return {
    async appendCompletedHand(hand) {
      const existing = await readEnvelopeOrNull<CompletedHandRecord>(
        db,
        STORE_COMPLETED_HANDS,
        hand.id,
        'completedHand',
        hand.id,
      );
      if (existing) {
        return;
      }
      await writeEnvelope(queue, db, STORE_COMPLETED_HANDS, hand);
    },

    async hasCompletedHand(id) {
      const existing = await readEnvelopeOrNull<CompletedHandRecord>(
        db,
        STORE_COMPLETED_HANDS,
        id,
        'completedHand',
        id,
      );
      return existing !== null;
    },

    async getSummary(): Promise<PlayerStatsSummary> {
      const hands = await loadHands();
      return derivePlayerStatsSummary(hands);
    },

    async reset() {
      await queue.enqueue(async () => {
        try {
          await db.clear(STORE_COMPLETED_HANDS);
        } catch (error) {
          throw new StorageUnavailableError('Unable to reset stats.', error);
        }
      });
    },
  };
}
